import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Header from "../components/Header";
import { Card, Checkbox, Upload, Input, Typography, AutoComplete, Flex, Table, Collapse, Modal } from "antd";
import type { SelectProps, TableProps } from "antd";
import { AudioOutlined, LoadingOutlined, SafetyCertificateTwoTone } from "@ant-design/icons";
import flowsheet from "../flowsheet.json"
import { useAuth0 } from "@auth0/auth0-react";
import { apiBaseUrl } from "../config";
import axios from "axios";
import { v4 as uuidv4 } from 'uuid';
import { TextAreaRef } from "antd/es/input/TextArea";
import { useNavigate } from "react-router-dom";
import Fuse from "fuse.js";

const { Dragger } = Upload
const { TextArea } = Input
const { Panel } = Collapse
const { Group: CheckboxGroup } = Checkbox

type Option = {
  id: string
  label: string
  text?: string
}

type Row = {
  id: string
  label: string
  options?: Array<Option>
}

type FlowsheetRow = {
  id: string
  name: string
  options: Array<FlowsheetOption>
}

type FlowsheetOption = {
  id: string
  name: string
  value?: string // for other comments case
}


type TableData = {
  parameter: string
  value: string
}

const columns: TableProps<TableData>['columns'] = [
  {
    title: 'Parameter',
    dataIndex: 'parameter',
    key: 'parameter',
  },
  {
    title: 'Update Values',
    dataIndex: 'value',
    key: 'value',
  },
];

const Title: React.FC<Readonly<{ title?: string }>> = (props) => (
  <Flex align="center" justify="space-between">
    {props.title}
    <a href="https://www.google.com/search?q=antd" target="_blank" rel="noopener noreferrer">
      more
    </a>
  </Flex>
)

const renderItem = (title: string, id: string) => ({
  id: id,
  value: title,
  label: (
    <Flex align="center" justify="space-between">
      {title}
    </Flex>
  ),
});

// TODO: Dont do this here,
// Keep a simpler interface for API, and do necessary mapping in backend before updating db
// Will allow multiple comments for later
const flowsheetDataToGroups = (data: Array<FlowsheetRow>) => {
  let groups : Array<any> = []
  data.forEach(row => {
    const ids = row.id.split(":")
    const groupId = ids[0]
    const rowId = ids[1]
    const existingGroup = groups.find(grp => grp.id === groupId)
    const optionIds = row.options.filter(opt => opt.id !== "-1").map(opt => opt.id.split(":")[2])
    const commentOpt = row.options.find(opt => opt.id === "-1" || opt.value)
    let comment = commentOpt ? { option_string: commentOpt?.value } : {}
    if (existingGroup) {
      groups = groups.map(grp => {
        if (grp.id === groupId) {
          return {
            ...grp,
            rows: grp.rows.concat({
              id: rowId,
              option_ids: optionIds,
              ...comment
            })
          }
        }
        return grp
      })
    } else {
      groups = groups.concat({
        id: groupId,
        rows: [{
          id: rowId,
          option_ids: optionIds,
          ...comment
        }]
      })
    }
  })
  return groups
}


const DataEntry = () => {
  const { isAuthenticated, user, getAccessTokenSilently } = useAuth0()

  const [audioFile, setAudioFile] = useState<File | null>(null)
  const [audioId, setAudioId] = useState("")
  const [audioUploading, setAudioUploading] = useState(false)
  const [transcription, setTranscription] = useState("")

  const [activeGroup, setActiveGroup] = useState<string>(flowsheet.templates[0].groups[0].id.toString())
  const [currRow, setCurrRow] = useState<Row | null>(null)
  const [optionsSelected, setOptionsSelected] = useState<Array<string>>([])
  const [optionsValue, setOptionsValue] = useState<string>("")
  const [flowsheetData, setFlowsheetData] = useState<Array<FlowsheetRow>>([])

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)

  const navigate = useNavigate()

  useEffect(() => {
    setFlowsheetData((old) => {
      const oldData = flowsheetData.find(row => row.id === currRow?.id)
      const ids = optionsSelected.length !== 0 ? optionsSelected?.[0].split(":") : []
      const optionsRowId = ids.length === 3 ? [ids[0], ids[1]].join(":") : ""
      let newSheet = old
      if (oldData) {
        newSheet = old.map(row => {
          if (row.id === currRow?.id) {
            if (currRow.options?.length === 0) {
              return {
                ...row,
                options: [{
                  id: "-1", // To handle rows with no options other than a comment
                  name: "",
                  value: optionsValue
                }]
              }
            }
            return {
              ...row,
              options: optionsSelected.map(opt => {
                const currentOption = currRow?.options?.find(currOpt => currOpt.id === opt)
                let optionsText = {}
                if (currentOption?.label.includes('Other (Comment)')) {
                  optionsText = { value: optionsValue }
                }
                return {
                  id: opt,
                  name: currentOption?.label,
                  ...optionsText
                }
              })
            } as FlowsheetRow
          }
          return row
        })
      }
      if (!oldData && ((optionsRowId === currRow?.id && optionsSelected.length !== 0) || optionsValue)) {
        console.log("optionsValue", optionsValue, optionsSelected)
        let newRow: any = {
          id: currRow?.id || "",
          name: currRow?.label || ""
        }
        if (currRow?.options?.length !== 0) {
          newRow = {
            id: currRow?.id || "",
            name: currRow?.label || "",
            options: optionsSelected.map(opt => {
              const currentOption = currRow?.options?.find(currOpt => currOpt.id === opt)
              let optionsText = {}
              if (currentOption?.label.includes('Other (Comment)')) {
                optionsText = { value: optionsValue }
              }
              return {
                id: opt,
                name: currentOption?.label || "",
                ...optionsText
              }
            })
          }
        } else {
          newRow = {
            id: currRow?.id || "",
            name: currRow?.label || "",
            options: [{
              id: "-1", // To handle rows with no options other than a comment
              name: "",
              text: optionsValue
            }]
          }
        }
        newSheet = old.concat([ newRow ])

      }
      return newSheet.filter(row => row.options.length !== 0)
    })
  }, [optionsSelected, optionsValue])

  useEffect(() => {
    const row = flowsheetData.find(r => r.id === currRow?.id)
    if (row) {
      setOptionsSelected(row?.options.map(opt => opt.id))
      const optionWithValue = row?.options.find(opt => opt.value)
      if (optionWithValue) {
        //@ts-ignore
        setOptionsValue(optionWithValue.value)
      }
    }
  }, [currRow])

  const submitFlowsheet = async () => {
    try {
      if (isAuthenticated && user) {
        const reqParams = {
          user: user.email,
          loc: audioId ? `${audioId}.m4a` : "",
          transcription: transcription,
          groups: flowsheetDataToGroups(flowsheetData)
        }
        const token = await getAccessTokenSilently()
        const response = await axios.post(`${apiBaseUrl}/api/new-entry`, reqParams, {
          headers: {
            Authorization: "Bearer " + token,
            'Content-Type': 'application/json'
          }
        })
        return response
      }
    } catch (error) {
      console.error('Error fetching user:', error)
    }
  }

  const validateForm = () => {
    if (audioFile === null && !transcription) {
      return false
    }
    if (flowsheetData.length === 0) {
      return false
    }
    return true
  }

  return (
    <div className="flex flex-col h-screen">
      <Header
        disabled={!validateForm()}
        onSubmit={() => setIsConfirmModalOpen(true)}
        buttonTitle="Submit"
      />
      <Modal
        title="Are you sure you want to submit?"
        open={isConfirmModalOpen}
        onOk={async () => {
          setIsConfirmModalOpen(false)
          const resp: any = await submitFlowsheet()
          console.log("RESP", resp)
          if (resp?.status === 200 && resp?.statusText === "OK") {
            navigate("/")
          }
          // if failed close modal, and show error
        }}
        onCancel={() => setIsConfirmModalOpen(false)}
      >
      </Modal>
      <div
        className="grow flex"
        style={{ maxHeight: 'calc(100% - 64px)'}}
      >
        <RawDataEntry
          audioFile={audioFile}
          setAudioFile={setAudioFile}
          setAudioId={setAudioId}
          audioUploading={audioUploading}
          setAudioUploading={setAudioUploading}
          transcription={transcription}
          setTranscription={setTranscription}
        />
        <FlowsheetEntry
          groups={flowsheet.templates[0].groups}
          activeGroup={activeGroup}
          setActiveGroup={setActiveGroup}
          currRow={currRow}
          setCurrRow={setCurrRow}
          setOptionsValue={setOptionsValue}
        />
        <OptionsEntry
          title={`Select Value${currRow ? " for " + currRow.label : ""}`}
          currRow={currRow}
          optionsSelected={optionsSelected}
          setOptionsSelected={setOptionsSelected}
          optionsValue={optionsValue}
          setOptionsValue={setOptionsValue}
        />
        <Preview
          flowsheetData={flowsheetData}
        />
      </div>
    </div>
  )
}

const RawDataEntry : React.FC<any> = ({
  audioFile,
  setAudioFile,
  audioUploading,
  setAudioUploading,
  transcription,
  setTranscription,
  setAudioId,
}) => {
  const { getAccessTokenSilently } = useAuth0()
  const handleAudioUpload = async (file: File) => {
    const formData = new FormData();
    const audId = uuidv4()
    setAudioId(audId)
    formData.append('audio', file)
    formData.append('id', audId)

    try {
      const token = await getAccessTokenSilently()
      await axios.post(`${apiBaseUrl}/api/upload-audio`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        }
      });
      console.log('File uploaded successfully')
    } catch (error) {
      setAudioId("")
      console.error('File upload failed')
    }
  }

  const props = {
    name: 'file',
    multiple: false, // Only allow one file
    accept: '.m4a', // Only accept .m4a files
    beforeUpload: async (file: File) => {
      const isM4A = file.type === 'audio/x-m4a' || file.name.endsWith('.m4a')
      if (!isM4A) {
        console.error(`${file.name} is not a .m4a file`)
        return Upload.LIST_IGNORE
      }
      setAudioUploading(true)
      await handleAudioUpload(file)
      setAudioUploading(false)
      setAudioFile(file)
      return false; // Prevent automatic upload
    },
    onRemove: () => {
      setAudioFile(null) // Clear the file
    },
  }

  return (
    <div className="m-8 w-2/12">
      <Card
        className="shadow-md rounded-lg h-fit"
        title="Add Audio Recording / Notes"
      >
        <Dragger {...props} className="flex flex-col justify-center items-center mb-10">
          <p className="ant-upload-drag-icon mt-6">
            {audioUploading &&
              <LoadingOutlined /> ||
              <AudioOutlined />
            }
          </p>
          {audioFile && !audioUploading &&
            <p className="ant-upload-text">{audioFile.name}</p>
          }
          {audioUploading &&
            <p className="ant-upload-text">Uploading File ...</p>
          }
          {!audioFile && !audioUploading &&
            <>
              <p className="ant-upload-text">Drag and drop a recording here, or click to select from finder</p>
              <p className="ant-upload-hint mb-6">Please ensure the file is .m4a file</p>
            </>
          }
        </Dragger>
        <Typography.Title level={2} className="text-center">
          OR
        </Typography.Title>
        <TextArea
          rows={14}
          disabled={!!audioFile || audioUploading}
          placeholder="Add notes here"
          className="mt-8 max-h-80 p-4 py-3"
          style={{ resize: "none" }}
          value={transcription}
          onChange={(e) => setTranscription(e.target.value)}
        />
      </Card>
    </div>
  )
}

const FlowsheetEntry : React.FC<any> = ({
  groups,
  activeGroup,
  setActiveGroup,
  setCurrRow,
  currRow,
  setOptionsValue
}) => {
  const defaultOptions = useMemo(() => groups.map((grp: any) => ({
    label: <Title title={grp.name} />,
    options: grp.rows.map((row: any) => renderItem(row.name, `${grp.id}:${row.id}`))
  })), [groups])

  const flattenedData = useMemo(() => {
    //@ts-ignore
    return groups.flatMap(group =>
      //@ts-ignore
      group.rows.map(row => ({ ...row, id: `${group.id}:${row.id}`, group: group.name }))
    )
  }, [groups])

  const fuse = useMemo(() => {
    return new Fuse(flattenedData, {
      keys: ['name', 'group'],
      threshold: 0.3,
    })
  }, [flattenedData])

  const [options, setOptions] = useState<SelectProps<string>['options']>(defaultOptions)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const panelRefs = useRef<Record<string, HTMLDivElement | null>>({})

  useLayoutEffect(() => {
    // Hacky way to use setTimeout to do this, this is because the ant-ui's accordian has a collapse animation, and the height needs to be updated before we can call scrollIntoview
    setTimeout(() => {
      const panelElement = panelRefs.current[activeGroup]
      const containerElement = containerRef.current

      if (panelElement && containerElement) {
        const headerElement = panelElement.parentElement?.parentElement?.previousElementSibling as HTMLElement
        headerElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }
    }, 250)
  }, [activeGroup])

  const onSearch = (searchText: string) => {
    if (!searchText) {
      setOptions(defaultOptions)
      return
    }

    const results = fuse.search(searchText);
    console.log("res", results)
    const groupedResults = results.reduce((acc, result) => {
      //@ts-ignore
      const { group, ...rest } = result.item;
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(rest);
      return acc;
    }, {} as Record<string, Row[]>);

    const newOptions = Object.entries(groupedResults).map(([groupName, rows]) => ({
      label: <Title title={groupName} />,
      //@ts-ignore
      options: rows.map(row => renderItem(row.name, row.id))
    }));

    setOptions(newOptions)
  }

  return (
    <div
      className="py-8 max-h-full w-4/12"
      style={{ maxWidth: "520px" }}
    >
      <div
        className="ant-card ant-card-bordered border shadow-md rounded-lg h-fit flex flex-col max-h-full overflow-y-hidden"
      >
        <div
          className="ant-card-head"
        >
          <div className="ant-card-head-wrapper">
            <div className="ant-card-head-title">Flowsheet Entry</div>
          </div>
        </div>
        <div
          className="overflow-y-hidden my-6 px-6 relative flex flex-col"
          style={{ maxHeight: 'calc(100% - 80px)' }}
        >
          <AutoComplete
            className="w-full mb-6"
            // popupClassName="certain-category-search-dropdown"
            popupMatchSelectWidth={500}
            options={options}
            size="large"
            onSelect={(val, selected) => {
              const ids = selected.id.split(":")
              const currGroup = groups.find((grp: any) =>
                grp.id == ids[0]
              )
              const selectedRow = currGroup.rows.find((row: any) =>
                row.id == ids[1]
              )
              setActiveGroup(ids[0])
              setCurrRow({
                id: selected.id,
                label: selected.value,
                options: selectedRow.options.map((opt: any) => ({
                  id: `${selected.id}:${opt.id}`,
                  label: opt.option
                }))
              })
              setOptionsValue("")
            }}
            onSearch={onSearch}
          >
            <Input.Search size="large" placeholder="Search Parameter" />
          </AutoComplete>
          <div
            className="overflow-y-auto border-y rounded-lg"
            ref={containerRef}
            style={{ maxHeight: 'calc(100% - 112px)', borderColor: "#d9d9d9" }}
          >
            <Collapse
              accordion
              className="rounded-lg"
              style={{ borderTop: "none", borderColor: "#d9d9d9" }}
              expandIconPosition="end"
              activeKey={activeGroup}
              onChange={(key) => {
                setActiveGroup(key?.[0])
                setCurrRow(null)
              }}
            >
              {groups.map((grp: any) =>
                <Panel
                  header={grp.name}
                  key={grp.id}
                  className={`text-lg font-medium p-0 ant-panel ${activeGroup === grp.id.toString() ? "selected" : "bg-white"}`}
                >
                  <div
                    ref={(el) => (panelRefs.current[grp.id.toString()] = el)}
                  >
                    {grp.rows.map((row: any) => {
                      const rowId = `${grp.id}:${row.id}`
                      return <p
                        className={`text-base px-4 py-1 cursor-pointer ${currRow?.id === rowId ? "font-medium selected" : "font-light"}`}
                        key={rowId}
                        onClick={() => {
                          setCurrRow({
                            id: rowId,
                            label: row.name,
                            options: row.options.map((opt: any) => ({
                              id: `${rowId}:${opt.id}`,
                              label: opt.option
                            }))
                          })
                          setOptionsValue("")
                        }}
                      >
                        {row.name}
                      </p>
                    }
                    )}

                  </div>
                </Panel>
              )}
            </Collapse>
          </div>
        </div>
      </div>
    </div>
  )
}

const OptionsEntry : React.FC<any> = ({
  title = "Select Value",
  currRow,
  optionsSelected,
  setOptionsSelected,
  optionsValue,
  setOptionsValue
}) => {
  return (
    <div className="m-8 grow flex flex-col justify-between w-2/12">
      <Card
        className="shadow-md rounded-lg h-fit"
        title={title}
      >
        <CheckboxGroup
          value={optionsSelected}
          onChange={(ev) => setOptionsSelected(ev)}
          className="flex flex-col space-y-2"
        >
          {currRow?.options?.map((option: any) => (
            option.label.includes('Other (Comment)') ? (
              <div key={option.id} className="flex items-center">
                <Checkbox value={option.id} className="mr-2" />
                <Input
                  value={optionsValue}
                  onChange={(e) => setOptionsValue(e.target.value)}
                  className="flex-1"
                  placeholder={option.label}
                  disabled={!optionsSelected.includes(option.id)}
                />
              </div>
            ) : (
              <Checkbox key={option.id} value={option.id}>
                {option.label}
              </Checkbox>
            )
          ))}
          {currRow?.options?.length === 0 &&
              <Input
                value={optionsValue}
                onChange={(e) => setOptionsValue(e.target.value)}
                className="flex-1"
                placeholder={currRow?.label}
              />
          }
        </CheckboxGroup>
      </Card>

    </div>
  )
}

const Preview : React.FC<any> = ({ flowsheetData }) => {
  return (
    <div
      className="mr-8 my-8 w-4/12"
    >
      <Card
        className="shadow-md rounded-lg h-fit"
        title="Preview"
      >
        <Table
          columns={columns}
          pagination={false}
          dataSource={flowsheetData.map((row : any) => ({
            parameter: row.name,
            value: row.options.map((opt : any) => opt.value ? `Comment - ${opt.value}` : opt.name).join(", ")
          }))}
        />
      </Card>
    </div>
  )
}

export default DataEntry